import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Grid, Box, FormLabel, FormGroup, FormControl, FilledInput, Button, Divider } from '@material-ui/core';
import ReactExport from 'react-data-export';
import dayjs from 'dayjs';

import Loader from '../../common/loader';
import DateRange from '../../common/date-range';

import AuthContext from '../../context';
import helpers from '../../utils/helpers';
import { get } from '../../utils/api-services';
import ViewTitle from '../../common/view-title';

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

const useStyles = makeStyles(() => ({
    root: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
    },
    buttonsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        marginBottom: 20
    },
    search: {
        backgroundColor: '#0d6efd',
        borderColor: '#0d6efd',
        '&:hover': {
            backgroundColor: '#0d6efd',
            borderColor: '#0d6efd',
            boxShadow: 'none',
        }
    },
    download: {
        marginRight: 20,
        backgroundColor: '#6E6E6E',
        borderColor: '#6E6E6E',
        '&:hover': {
            backgroundColor: '#6E6E6E',
            borderColor: '#6E6E6E',
            boxShadow: 'none'
        }
    }
}));

const Reports = ({ permissions }) => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const classes = useStyles();
    const [state, setState] = useState({
        loading: true,
        filter: {
            startDate: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
            endDate: dayjs().format('YYYY-MM-DD'),
            dateRange: `${dayjs().subtract(1, 'day').format('MM/DD/YYYY')} - ${dayjs().format('MM/DD/YYYY')}`
        },
        detailGeneralV1: [],
        detailProcessedV1: [],
        inventoryGeneralV1: [],
        inventoryProcessedV1: [],
        detailGeneralV2: [],
        detailProcessedV2: [],
        inventoryGeneralV2: [],
        inventoryProcessedV2: [],
        rangeModal: {
            open: false,
            range: [
                {
                    startDate: new Date(dayjs().subtract(1, 'day').toDate()),
                    endDate: new Date(),
                    key: 'main'
                }
            ]
        },
        permissions: {}
    });

    useEffect(() => {
        setState(prev => ({ ...prev, permissions }));
    }, [permissions]);

    useEffect(() => {
        window.document.title = 'Reports';

        setState(prev => ({ ...prev, loading: false }));
    }, []);

    useEffect(() => {
        setState(prev => ({ ...prev, detailGeneralV1: [], detailProcessedV1: [], inventoryGeneralV1: [], inventoryProcessedV1: [], detailGeneralV2: [], detailProcessedV2: [], inventoryGeneralV2: [], inventoryProcessedV2: [] }));
    }, [state.filter]);

    const handleDateChange = item => {
        const { startDate, endDate } = item.main;
        const dateRange = `${dayjs(startDate.toISOString()).format('MM/DD/YYYY')} - ${dayjs(endDate.toISOString()).format('MM/DD/YYYY')}`;
        setState(prev => ({
            ...prev,
            filter: {
                ...prev.filter,
                dateRange,
                startDate: dayjs(startDate.toISOString()).format('YYYY-MM-DD'),
                endDate: dayjs(endDate.toISOString()).format('YYYY-MM-DD')
            },
            rangeModal: { ...prev.rangeModal, range: [item.main] }
        }));
    }

    const handleModal = () => setState(prev => ({ ...prev, rangeModal: { ...prev.rangeModal, open: !prev.rangeModal.open } }));

    const searchDetail = async (type, version) => {
        const response = await get('/reports/searchProcessDetail', { ...state.filter, type, version });
        if (response.status === 200) {
            setState(prev => ({ ...prev, [`detail${type}${version}`]: response?.data ?? [] }));

            if (!response?.data[0]) showToast('There are no data with these filters', 'warning');
        } else {
            console.log('[Reports] searchDetail error', response);
            helpers.failRequest(response, showToast, signOut);
        }
    }

    const searchInventory = async (type, version) => {

        const response = await get('/reports/searchProcessInventory', { ...state.filter, type, version });
        if (response.status === 200) {
            setState(prev => ({ ...prev, [`inventory${type}${version}`]: response?.data ?? [] }));

            if (!response?.data[0]) showToast('There are no data with these filters', 'warning');
        } else {
            console.log('[Reports] searchInventory error', response);
            helpers.failRequest(response, showToast, signOut);
        }
    }

    const search = async (type, version) => {
        showLoader(true);

        if (type === 'General' || type === 'Processed') {
            await Promise.any([searchDetail(type, version), searchInventory(type, version)]);
        } else {
            showToast('Type error', 'error');
        }

        showLoader(false);
    }

    if (state.loading) {
        return <Loader />;
    }

    return (
        <div className={classes.root}>
            <ViewTitle sedeFlag title='Reports' />
            <Grid container>
                <Grid item xs={6} lg={4}>
                    <FormGroup onClick={handleModal}>
                        <FormLabel>{'Date range'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                readOnly
                                type='text'
                                placeholder='Date range'
                                value={state.filter.dateRange}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
            </Grid>

            <ViewTitle title='General reports' />

            <div className={classes.buttonsContainer}>
                {
                    /* Detail Report */
                    state.detailGeneralV2.length > 0 &&
                    <ExcelFile
                        filename={`${dayjs().format('YYYY-MM-DD')}-process-detail-report-general-v2`}
                        element={<Button variant='contained' className={classes.download}>{'Download Detail Report'}</Button>}
                    >
                        <ExcelSheet data={state.detailGeneralV2} name='Ticket process detail v2 report'>
                            <ExcelColumn label='Ticket No.' value='code' />
                            <ExcelColumn label='Ticket date' value='date' />
                            <ExcelColumn label='Sede code' value='sede.code' />
                            <ExcelColumn label='Sede name' value='sede.name' />
                            <ExcelColumn label='Pack' value='pack' />
                            <ExcelColumn label='Total ticket units' value='ticket_qty' />
                            <ExcelColumn label='Product code' value='product.code' />
                            <ExcelColumn label='Product name' value='product.name' />
                            <ExcelColumn label='Product category code' value='product.category.code' />
                            <ExcelColumn label='Product category name' value='product.category.name' />
                            <ExcelColumn label='Product type name' value='product.type.name' />
                            <ExcelColumn label='Product divisivility' value='product_divisibility.type.name' />
                            <ExcelColumn label='Process date' value='processes.date' />
                            <ExcelColumn label='Process employee code' value='processes.employee.code' />
                            <ExcelColumn label='Process employee name' value='processes.employee.name' />
                            <ExcelColumn label='Process item name' value='processes.item.name' />
                            <ExcelColumn label='Process total qty' value='processes.total_qty' />
                            <ExcelColumn label='Process pending qty' value='processes.pending_qty' />
                            <ExcelColumn label='Process status name' value='processing_status' />
                        </ExcelSheet>
                    </ExcelFile>
                }

                {
                    /* Inventory Report */
                    state.inventoryGeneralV2.length > 0 &&
                    <ExcelFile
                        filename={`${dayjs().format('YYYY-MM-DD')}-process-inventory-report-general-v2`}
                        element={<Button variant='contained' className={classes.download}>{'Download Inventory Report'}</Button>}
                    >
                        <ExcelSheet data={state.inventoryGeneralV2} name='Ticket process inventory v2 report'>
                            <ExcelColumn label='Ticket No.' value='code' />
                            <ExcelColumn label='Ticket date' value='date' />
                            <ExcelColumn label='Processing start date' value='process_start_date' />
                            <ExcelColumn label='Sede code' value='sede.code' />
                            <ExcelColumn label='Sede name' value='sede.name' />
                            <ExcelColumn label='Location code' value='location.code' />
                            <ExcelColumn label='Location name' value='location.name' />
                            <ExcelColumn label='Total ticket units' value='ticket_qty' />
                            <ExcelColumn label='Pack' value='pack' />
                            <ExcelColumn label='Product code' value='product.code' />
                            <ExcelColumn label='Product name' value='product.name' />
                            <ExcelColumn label='Product category code' value='product.category.code' />
                            <ExcelColumn label='Product category name' value='product.category.name' />
                            <ExcelColumn label='Product type name' value='product.type.name' />
                            <ExcelColumn label='Product divisivility' value='product_divisibility.type.name' />
                            <ExcelColumn label='Processed total qty' value='processed_qty' />
                            <ExcelColumn label='Process pending qty' value='pending_qty' />
                            <ExcelColumn label='Process status name' value='processing_status' />
                            <ExcelColumn label='Checkpoint date' value='checkpoint_date' />
                            <ExcelColumn label='Time without being processed' value='days_without_being_processed' />
                        </ExcelSheet>
                    </ExcelFile>
                }

                <Button
                    variant='contained'
                    className={classes.search}
                    onClick={() => search('General', 'V2')}
                >
                    {'Search v2 (general)'}
                </Button>
            </div>

            <Divider />
            <ViewTitle title='Processed reports' />

            <div className={classes.buttonsContainer}>
                {
                    /* Detail Report */
                    state.detailProcessedV2.length > 0 &&
                    <ExcelFile
                        filename={`${dayjs().format('YYYY-MM-DD')}-process-detail-report-processed-v2`}
                        element={<Button variant='contained' className={classes.download}>{'Download Detail Report'}</Button>}
                    >
                        <ExcelSheet data={state.detailProcessedV2} name='Ticket process detail v2 report'>
                            <ExcelColumn label='Ticket No.' value='code' />
                            <ExcelColumn label='Ticket date' value='date' />
                            <ExcelColumn label='Sede code' value='sede.code' />
                            <ExcelColumn label='Sede name' value='sede.name' />
                            <ExcelColumn label='Pack' value='pack' />
                            <ExcelColumn label='Total ticket units' value='ticket_qty' />
                            <ExcelColumn label='Product code' value='product.code' />
                            <ExcelColumn label='Product name' value='product.name' />
                            <ExcelColumn label='Product category code' value='product.category.code' />
                            <ExcelColumn label='Product category name' value='product.category.name' />
                            <ExcelColumn label='Product type name' value='product.type.name' />
                            <ExcelColumn label='Product divisivility' value='product_divisibility.type.name' />
                            <ExcelColumn label='Process date' value='processes.date' />
                            <ExcelColumn label='Process employee code' value='processes.employee.code' />
                            <ExcelColumn label='Process employee name' value='processes.employee.name' />
                            <ExcelColumn label='Process item name' value='processes.item.name' />
                            <ExcelColumn label='Process total qty' value='processes.total_qty' />
                            <ExcelColumn label='Process pending qty' value='processes.pending_qty' />
                            <ExcelColumn label='Process status name' value='processing_status' />
                        </ExcelSheet>
                    </ExcelFile>
                }

                {
                    /* Inventory Report */
                    state.inventoryProcessedV2.length > 0 &&
                    <ExcelFile
                        filename={`${dayjs().format('YYYY-MM-DD')}-process-inventory-report-processed-v2`}
                        element={<Button variant='contained' className={classes.download}>{'Download Inventory Report'}</Button>}
                    >
                        <ExcelSheet data={state.inventoryProcessedV2} name='Ticket process inventory v2 report'>
                            <ExcelColumn label='Ticket No.' value='code' />
                            <ExcelColumn label='Ticket date' value='date' />
                            <ExcelColumn label='Processing start date' value='process_start_date' />
                            <ExcelColumn label='Sede code' value='sede.code' />
                            <ExcelColumn label='Sede name' value='sede.name' />
                            <ExcelColumn label='Location code' value='location.code' />
                            <ExcelColumn label='Location name' value='location.name' />
                            <ExcelColumn label='Total ticket units' value='ticket_qty' />
                            <ExcelColumn label='Pack' value='pack' />
                            <ExcelColumn label='Product code' value='product.code' />
                            <ExcelColumn label='Product name' value='product.name' />
                            <ExcelColumn label='Product category code' value='product.category.code' />
                            <ExcelColumn label='Product category name' value='product.category.name' />
                            <ExcelColumn label='Product type name' value='product.type.name' />
                            <ExcelColumn label='Product divisivility' value='product_divisibility.type.name' />
                            <ExcelColumn label='Processed total qty' value='processed_qty' />
                            <ExcelColumn label='Process pending qty' value='pending_qty' />
                            <ExcelColumn label='Process status name' value='processing_status' />
                            <ExcelColumn label='Checkpoint date' value='checkpoint_date' />
                            <ExcelColumn label='Time without being processed' value='days_without_being_processed' />
                        </ExcelSheet>
                    </ExcelFile>
                }

                <Button
                    variant='contained'
                    className={classes.search}
                    onClick={() => search('Processed', 'V2')}
                >
                    {'Search v2 (processed)'}
                </Button>
            </div>

            <DateRange
                open={state.rangeModal.open}
                range={state.rangeModal.range}
                onClose={handleModal}
                onChange={handleDateChange}
                minDate={dayjs('2022-01-01').toDate()}
            />
        </div>
    );
}

export default Reports;